import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'utils/axios';
import { useWorkspace } from 'contexts/WorkspaceContext';
import { Property } from 'hooks/useProperties';

interface UsePropertyParams {
  documentId: string | undefined;
}

interface UsePropertyResult {
  property: Property | null;
  loading: boolean;
  error: any;
  refetch: () => void;
}

const resolveAssessmentStatus = (assessment: any): string | null => {
  const amount = Number(assessment?.amount);
  const paidAmount = Number(assessment?.paid_amount);
  const rawStatus = assessment?.assestment_status ?? assessment?.status ?? null;

  if (assessment?.paid_at) {
    return 'paid';
  }

  if (Number.isFinite(amount) && Number.isFinite(paidAmount)) {
    if (paidAmount >= amount && amount > 0) {
      return 'paid';
    }

    if (paidAmount > 0) {
      return 'partial';
    }
  }

  return rawStatus;
};

export default function useProperty({ documentId }: UsePropertyParams): UsePropertyResult {
  const { loading: workspaceLoading } = useWorkspace();
  const [property, setProperty] = useState<Property | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<any>(null);

  const query = useMemo(
    () => `
      query Property($filters: PropertyFiltersInput) {
        properties_connection(filters: $filters, pagination: { start: 0, limit: 1 }) {
          nodes {
            documentId
            address_1
            address_2
            city
            state
            zipcode
            section
            lot
            phase
            block
            hasViolations
            hasTickets
            community {
              documentId
              name
            }
            occupants {
              primary
              documentId
              user {
                documentId
                firstName
                lastName
                email
              }
              occupant_type {
                name
                documentId
              }
            }
            tickets {
              documentId
              type
              subject
              ticketStatus {
                name
                documentId
              }
            }
            assessments(sort: ["due_date:desc"], pagination: { start: 0, limit: 100 }) {
              documentId
              amount
              paid_amount
              due_date
              paid_at
              assestment_status
              assessment_type {
                documentId
                name
              }
              budget {
                documentId
                fiscal_year
              }
            }
          }
        }
      }
    `,
    []
  );

  const fetchData = useCallback(async () => {
    if (workspaceLoading || !documentId) {
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await axios.post('/graphql', {
        query,
        variables: {
          filters: { documentId: { eq: documentId } }
        }
      });

      if (response.data?.errors) {
        throw new Error(response.data.errors[0]?.message || 'Failed to fetch property');
      }

      const nodes = response.data?.data?.properties_connection?.nodes || [];
      const propertyNode = nodes[0] ?? null;
      if (!propertyNode) {
        setProperty(null);
      } else {
        const assessments = Array.isArray(propertyNode.assessments)
          ? propertyNode.assessments.map((assessment: any) => ({
              ...assessment,
              paid_amount: assessment.paid_amount != null ? Number(assessment.paid_amount) : null,
              status: resolveAssessmentStatus(assessment),
            }))
          : [];

        setProperty({
          ...propertyNode,
          assessments,
        });
      }
    } catch (err: any) {
      setError(err);
      setProperty(null);
    } finally {
      setLoading(false);
    }
  }, [query, documentId, workspaceLoading]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { property, loading, error, refetch: fetchData };
}
